import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MealAutocomplete } from "@/components/MealAutocomplete";
import { api, errMsg } from "@/lib/api";

export const MealExtraDialog = ({ open, onOpenChange, onSubmit, busy }) => {
  const [name, setName] = useState("");
  const [picked, setPicked] = useState(null);
  const [macros, setMacros] = useState({ kcal: "", protein: "", carbs: "", fat: "", notes: "", time: "" });
  const [items, setItems] = useState([]);
  const [estimating, setEstimating] = useState(false);
  const [estimateHint, setEstimateHint] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setPicked(null);
    setEstimateHint("");
    setMacros({ kcal: "", protein: "", carbs: "", fat: "", notes: "", time: "" });
    api.get("/nutrition/meal-suggestions", { params: { slot: "merienda" } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [open]);

  const applyPick = (item) => {
    setPicked(item);
    if (!item) {
      setEstimateHint("Registro nuevo: calcula macros o ajústalos a mano.");
      return;
    }
    setMacros((p) => ({
      ...p,
      kcal: item.kcal != null ? String(item.kcal) : p.kcal,
      protein: item.protein != null ? String(item.protein) : p.protein,
      carbs: item.carbs != null ? String(item.carbs) : p.carbs,
      fat: item.fat != null ? String(item.fat) : p.fat,
      notes: item.notes || p.notes,
    }));
    setEstimateHint(item.source === "personal" ? "De un registro tuyo anterior." : "Del catálogo. Ajusta si la ración fue distinta.");
  };

  const estimateMacros = async () => {
    const n = name.trim();
    if (n.length < 2) return toast.error("Escribe primero qué tomaste o comiste");
    setEstimating(true);
    setEstimateHint("");
    try {
      const { data } = await api.post("/nutrition/estimate-macros", { name: n, slot: "merienda", use_ai: true });
      setMacros((p) => ({
        ...p,
        kcal: String(data.kcal),
        protein: String(data.protein),
        carbs: String(data.carbs),
        fat: String(data.fat),
      }));
      setPicked(null);
      const src = data.source === "ai" ? "IA" : data.source === "catalog" ? "catálogo" : "estimación";
      setEstimateHint(`Sugerido (${src}${data.matched ? `: ${data.matched}` : ""}). Puedes ajustar.`);
      toast.success("Macros sugeridos — revísalos si quieres");
    } catch (e) { toast.error(errMsg(e)); } finally { setEstimating(false); }
  };

  const submit = () => {
    const n = name.trim();
    if (!n) return toast.error("Indica qué tomaste o comiste");
    if (!macros.kcal) return toast.error("Indica al menos las calorías (o pulsa calcular macros)");
    onSubmit({
      name: n,
      kcal: Number(macros.kcal),
      protein: macros.protein ? Number(macros.protein) : 0,
      carbs: macros.carbs ? Number(macros.carbs) : 0,
      fat: macros.fat ? Number(macros.fat) : 0,
      notes: macros.notes?.trim() || undefined,
      time: macros.time || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] min-w-0 overflow-x-hidden overflow-y-auto rounded-3xl sm:max-w-md" data-testid="meal-extra-dialog">
        <DialogHeader className="pr-6">
          <DialogTitle className="font-display text-xl">Añadir extra del día</DialogTitle>
          <DialogDescription className="break-words">
            Cervezas, un tentempié, algo fuera de hora… Se suma al día sin sustituir el menú planeado.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-stone-600">Qué tomaste / comiste</label>
            <MealAutocomplete
              value={name}
              onChange={(v) => { setName(v); setEstimateHint(""); }}
              onPick={applyPick}
              items={items}
              placeholder="Ej.: 2 cervezas, café con leche, nachos…"
              testid="meal-extra-autocomplete"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-stone-600">Hora (opcional)</label>
            <Input type="time" value={macros.time} onChange={(e) => setMacros((p) => ({ ...p, time: e.target.value }))} className="rounded-2xl" data-testid="meal-extra-time" />
          </div>
          <button
            type="button"
            disabled={estimating || name.trim().length < 2}
            onClick={estimateMacros}
            data-testid="meal-extra-estimate-btn"
            className="w-full rounded-full border border-food/40 bg-food-soft py-2.5 text-xs font-semibold text-food-ink transition-transform active:scale-95 disabled:opacity-50"
          >
            {estimating ? "Calculando…" : "Calcular macros aprox."}
          </button>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Calorías (kcal)</label>
              <Input type="number" inputMode="numeric" value={macros.kcal} onChange={(e) => setMacros((p) => ({ ...p, kcal: e.target.value }))} data-testid="meal-extra-kcal" className="rounded-2xl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Proteína (g)</label>
              <Input type="number" inputMode="numeric" value={macros.protein} onChange={(e) => setMacros((p) => ({ ...p, protein: e.target.value }))} data-testid="meal-extra-protein" className="rounded-2xl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Hidratos (g)</label>
              <Input type="number" inputMode="numeric" value={macros.carbs} onChange={(e) => setMacros((p) => ({ ...p, carbs: e.target.value }))} data-testid="meal-extra-carbs" className="rounded-2xl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Grasas (g)</label>
              <Input type="number" inputMode="numeric" value={macros.fat} onChange={(e) => setMacros((p) => ({ ...p, fat: e.target.value }))} data-testid="meal-extra-fat" className="rounded-2xl" />
            </div>
          </div>
          {estimateHint && <p className="text-xs text-stone-500">{estimateHint}</p>}
          <Textarea placeholder="Notas (opcional)" value={macros.notes} onChange={(e) => setMacros((p) => ({ ...p, notes: e.target.value }))} className="max-w-full rounded-2xl" data-testid="meal-extra-notes" />
          <button disabled={busy} onClick={submit} data-testid="meal-extra-submit" className="w-full rounded-full bg-food py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60">
            {busy ? "Guardando…" : "Añadir al día"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
